(() => {
  if (window.__jfKlaviyoScrollGuardV2Installed) return;
  window.__jfKlaviyoScrollGuardV2Installed = true;
  window.__jfKlaviyoScrollGuardInstalled = true;

  const lockClass = 'klaviyo-prevent-body-scrolling';
  const modalSelector = '[data-kl-scroll-locking-modal="true"], [role="dialog"][class*="kl-private-reset-css"]';
  const dismissedAttribute = 'data-jf-klaviyo-dismissed';
  const otherDialogSelector = '[role="dialog"], [aria-modal="true"], dialog[open], modal-content[open], drawer-content[open], popover-content[open], #iubenda-cs-banner, #iubenda-cs-preferences-wrapper, .gpsr-compliance-wrapper';
  const checkInterval = 200;
  const missingModalGracePeriod = 600;
  let checkTimer;
  let missingModalSince = null;
  let recoveryUntil = 0;
  const modals = new Map();

  function isVisible(modal) {
    if (!modal.isConnected || modal.getClientRects().length === 0) return false;

    for (let element = modal; element; element = element.parentElement) {
      if (element.hidden) return false;

      const style = window.getComputedStyle(element);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        style.contentVisibility === 'hidden'
      ) return false;
    }

    return true;
  }

  function getState(modal) {
    if (!modals.has(modal)) {
      modals.set(modal, { closeAt: 0, phase: '', dismissed: false, ignoreClosingUntil: 0 });
    }
    return modals.get(modal);
  }

  function formId(modal) {
    return modal.querySelector('form[data-testid^="klaviyo-form-"]')?.getAttribute('data-testid').slice(13);
  }

  function reopen(modal, state) {
    state.closeAt = 0;
    state.dismissed = false;
    state.ignoreClosingUntil = window.performance.now() + 1000;
    modal.removeAttribute(dismissedAttribute);
    missingModalSince = null;
  }

  function beginClose(modal, state) {
    if (state.closeAt || state.dismissed) return;
    const style = window.getComputedStyle(modal);
    const durations = style.animationDuration.split(',').map((value) => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000));
    const delays = style.animationDelay.split(',').map((value) => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000));
    const animationTime = Math.max(0, ...durations.map((duration, index) => duration + (delays[index % delays.length] || 0)));
    state.closeAt = window.performance.now() + Math.min(2000, Math.max(600, animationTime + 200));
    recoveryUntil = Math.max(recoveryUntil, state.closeAt + 3000);
  }

  function hasOtherLockOwner() {
    const root = document.documentElement;
    if (root.classList.contains('lock-all')) return true;
    if (root.classList.contains('lock-mobile') && window.matchMedia('(max-width: 999px)').matches) return true;

    return Array.from(document.querySelectorAll(otherDialogSelector)).some((dialog) => {
      if (dialog.matches(modalSelector) || dialog.closest(modalSelector)) return false;
      return isVisible(dialog);
    });
  }

  function releaseInlineLocks() {
    if (hasOtherLockOwner()) return;

    for (const element of [document.documentElement, document.body]) {
      for (const property of ['overflow', 'overflow-y']) {
        if (/^(hidden|clip)$/.test(element.style.getPropertyValue(property).trim())) {
          element.style.removeProperty(property);
        }
      }
    }
  }

  function reconcile() {
    window.clearTimeout(checkTimer);
    checkTimer = undefined;

    const body = document.body;
    if (!body) return;
    const now = window.performance.now();
    let modalIsOpen = false;
    let closing = false;

    for (const [modal] of modals) {
      if (!modal.isConnected) modals.delete(modal);
    }

    for (const modal of document.querySelectorAll(modalSelector)) {
      const state = getState(modal);
      const content = modal.querySelector('[data-testid="POPUP"], [data-testid="FULLSCREEN"], [data-testid="FLYOUT"]');
      const fadingOut = modal.style.animationName.includes('klaviyo-fadeout') ||
        (content?.style.animationDirection === 'reverse' && content.style.animationName.includes('klaviyo-'));
      const phase = fadingOut ? 'closing' : modal.style.animationName.includes('klaviyo-fadein') ? 'opening' : '';

      if (phase === 'opening' && state.phase === 'closing') reopen(modal, state);
      state.phase = phase;
      if (fadingOut && now >= state.ignoreClosingUntil) beginClose(modal, state);

      if (state.closeAt && now >= state.closeAt) {
        if (modal.contains(document.activeElement)) document.activeElement.blur();
        modal.setAttribute(dismissedAttribute, 'true');
        state.dismissed = true;
        state.closeAt = 0;
      }

      if (state.closeAt) closing = true;
      if (!state.dismissed && isVisible(modal)) modalIsOpen = true;
    }

    if (modalIsOpen) {
      missingModalSince = null;
    } else if (body.classList.contains(lockClass)) {
      if (missingModalSince === null) missingModalSince = now;

      if (now - missingModalSince >= missingModalGracePeriod) {
        body.classList.remove(lockClass);
        recoveryUntil = Math.max(recoveryUntil, now + 3000);
        missingModalSince = null;
      }
    } else {
      missingModalSince = null;
    }

    if (!modalIsOpen && recoveryUntil > now) releaseInlineLocks();

    if (modalIsOpen || closing || body.classList.contains(lockClass) || recoveryUntil > now) {
      checkTimer = window.setTimeout(reconcile, checkInterval);
    }
  }

  function requestCheck() {
    if (checkTimer === undefined) checkTimer = window.setTimeout(reconcile, 0);
  }

  function init() {
    const style = document.createElement('style');
    style.textContent = `[${dismissedAttribute}="true"] { display: none !important; pointer-events: none !important; }`;
    document.head.appendChild(style);

    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'childList' ||
        record.target === document.body || record.target === document.documentElement ||
        record.target.closest(modalSelector) || recoveryUntil > window.performance.now())) requestCheck();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open', 'data-kl-scroll-locking-modal']
    });

    window.addEventListener('klaviyoForms', (event) => {
      const detail = event.detail;
      if (!detail || (detail.type !== 'open' && detail.type !== 'close')) return;

      for (const modal of document.querySelectorAll(modalSelector)) {
        if (detail.formId && formId(modal) !== detail.formId) continue;
        const state = getState(modal);
        if (detail.type === 'open') {
          reopen(modal, state);
          state.phase = '';
        } else {
          beginClose(modal, state);
        }
      }
      reconcile();
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('.klaviyo-close-form');
      const modal = button?.closest(modalSelector);
      if (modal) {
        beginClose(modal, getState(modal));
        requestCheck();
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || hasOtherLockOwner()) return;
      const modal = Array.from(document.querySelectorAll(modalSelector)).reverse().find(isVisible);
      const button = modal?.querySelector('.klaviyo-close-form');
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        beginClose(modal, getState(modal));
        button.click();
        requestCheck();
      }
    }, true);
    window.addEventListener('pageshow', reconcile);
    window.addEventListener('focus', reconcile);
    document.addEventListener('visibilitychange', reconcile);
    document.addEventListener('shopify:section:load', reconcile);
    document.addEventListener('shopify:section:unload', reconcile);

    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
